import React, { useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { NewMainHeaderProps } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import { IssueLayoutRequestTypeOwnerType } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/issue/issue-layout-owner.tsx';
import { MainHeaderTmp } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/main-header-tmp/index.tsx';
import { editIssueTypeMessages } from '@atlassian/jira-project-settings-product-features-jsm-common/src/common/messages.tsx';
import { ConfigButton as RequestTypeSecurityConfigButton } from '@atlassian/jira-servicedesk-request-type-security/src/ui/config-button/index.tsx';
import { JSD_IS_EMAIL_REQUEST_TYPE } from '../../common/constants.tsx';
import messages, { messagesIssueTermRefresh } from './messages.tsx';

export const MainHeader = (props: NewMainHeaderProps) => {
	const { extraDefinerId, issueLayoutOwners, project } = props;
	const issueType = issueLayoutOwners.find(({ data: { id } }) => id === extraDefinerId);
	if (!issueType || issueType.type !== IssueLayoutRequestTypeOwnerType) {
		throw new Error("Can't find current request type data");
	}
	const { formatMessage } = useIntl();
	const { properties } = issueType.data;
	const isEmailRequestType = properties && properties[JSD_IS_EMAIL_REQUEST_TYPE];

	const requestTypeSecurityConfigButton =
		issueType.data.externalUuid && issueType.data.restrictionStatus ? (
			<RequestTypeSecurityConfigButton
				{...(fg('concurrent-rendering-fix-unique-list-key')
					? { key: `RequestTypeSecurityConfigButton-${project.projectId}` }
					: {})}
				projectId={project.projectId}
				projectKey={project.projectKey}
				requestType={{
					externalId: issueType.data.externalUuid,
					restrictionStatus: issueType.data.restrictionStatus,
					issueSecurityStatus: 'INACTIVE',
					usedByEmailSettings: !!isEmailRequestType,
				}}
				features={['REQUEST_TYPE_PERMISSIONS']}
				showText
				skipOnboarding
			/>
		) : null;

	const headerMessages = useMemo(
		() => ({
			...messages,
			...(fg('itsm-issue-renaming') ? messagesIssueTermRefresh : {}),
			editNameButtonLabel: editIssueTypeMessages.editNameButtonLabel,
			editNameInputLabel: editIssueTypeMessages.editNameInputLabel,
		}),
		[],
	);

	return (
		<>
			<MainHeaderTmp
				{...props}
				issueType={issueType}
				messages={headerMessages}
				extraActions={[requestTypeSecurityConfigButton, ...(props.extraActions ?? [])]}
			/>
			{isEmailRequestType ? (
				<BlockWrapper>{formatMessage(messages.emailRequestTypeDescription)}</BlockWrapper>
			) : null}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BlockWrapper = styled.div<{
	children?: ReactNode;
}>({
	/* Ensure block elements in the header have correct vertical spacing */
	marginBottom: token('space.200'),
});
